import Cookies from "js-cookie";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { API_ENDPOINTS } from "../api-shell/api-provider";
import ApiGet from "../../../../api-controller-mod/api-serv/get-api/getApi";
import apiGetFromAuth from "../../../../api-controller-mod/api-serv/get-api/getApiFromAuth";
const AUTH_URL = `${process.env.REACT_APP_URL_AUTHENTICATION}`;
const URL_AUTHEN = `${AUTH_URL}/login`;

interface AuthCookProps {
  tokenPass: string
}

interface CookiesContextType {
  authCook: (tokenPass: AuthCookProps) => void;
  authReady: boolean;
  LocalStorageReseter: () => void;
  logoutController: () => void;
  deleteDetection: () => void;
}

// --- Context ---
const CookiesContext = createContext<CookiesContextType | undefined>(undefined);

// --- Provider ---
type CookiesProviderProps = { children: ReactNode };

export const CookiesProvider: React.FC<CookiesProviderProps> = ({ children }) => {
  const [authReady, setAuthReady] = useState(true);


  useEffect(() => {
    getDetailUsr()
    getDetailInfo()
  }, [])


  const authCook = useCallback((tokenPass: AuthCookProps) => {
    const expiryInSeconds = 86400;
    const expiryCookie = { expires: 1 };

    // Save token
    Cookies.set("access_token", tokenPass.tokenPass, expiryCookie);

    // Notify system that token is now ready
    setAuthReady(true);

    const expirationTimestamp = Date.now() + expiryInSeconds * 1000;
    localStorage.setItem("session_expiration", expirationTimestamp.toString());
    scheduleAutoLogout(expiryInSeconds * 1000);

  }, []);


  const scheduleAutoLogout = (delayMs: number) => {
    setTimeout(() => {
      const currentUrl = globalThis.location.href;
      const redirectUrl = `${URL_AUTHEN}?redirect_uri=${encodeURIComponent(currentUrl)}`;

      globalThis.location.href = redirectUrl;
    }, delayMs);
  };

  const LocalStorageReseter = useCallback(() => {

  }, [])

  const getDetailUsr = async () => {
    try {
      const res = await ApiGet<{ data: { userId: string } }>(
        API_ENDPOINTS.GET_USER_ID
      );
      const usrId = res?.data?.data?.userId
      Cookies.set("userUuid", usrId);
    } catch (error) {
      console.error("Failed to fetch user id", error);
    }
  }

  const getDetailInfo = async () => {
    try {
      const res = await apiGetFromAuth(
        API_ENDPOINTS.GET_USER_INFO
      );

      const data = res?.data?.data

      Cookies.set("username", data?.username);
      Cookies.set("phoneNum", data?.phone);
      Cookies.set("email", data?.email);
      Cookies.set("usrRole", data?.role);
      Cookies.set("profile", data?.profile);

    } catch (error) {
      console.error("Failed to fetch user info", error);
    }
  };

  const deleteDetection = useCallback(() => {
    localStorage.setItem("isDelete", "false");
  }, []);

  const logoutController = useCallback(() => {
    LocalStorageReseter()
    Cookies.remove("access_token")
    Cookies.remove("refresh_token")
    Cookies.remove("email")
    Cookies.remove("username")
    Cookies.remove("phoneNum")
    Cookies.remove("usrRole")
    Cookies.remove("profile")
    Cookies.remove("userUuid")
    Cookies.remove("userId")
    localStorage.removeItem("session_expiration")
  }, [])

  // === Memoize context value ===
  const value = useMemo(
    () => ({
      authCook,
      authReady,
      LocalStorageReseter,
      logoutController,
      deleteDetection, 
    }),
    [
      authCook,
      authReady,
      LocalStorageReseter,
      logoutController,
      deleteDetection, 
    ]
  );

  return <CookiesContext.Provider value={value}>{children}</CookiesContext.Provider>;
};

// --- Hook ---
export const useCookies = (): CookiesContextType => {
  const context = useContext(CookiesContext);
  if (!context) throw new Error("useCookies must be used within CookiesProvider");
  return context;
};
